import type { BlobClient } from '@jbroll/rowboat-client';
import { useBlobUrl } from '@jbroll/rowboat-react';
import { useEffect, useMemo, useState } from 'react';
import { usePhotoUploads } from '@/components/PhotoUploads';
import { getBlobClient } from '@/lib/blobs';
import { fetchPhotoBlob, PhotoFetchError } from '@/services/fetchPhoto';
import { sessionThumbUrl } from '@/services/sessionThumbs';
import { useDownloadRetry } from './useDownloadRetry';

// useBlobUrl swallows failures, so the client reports them on the way through.
function reportingClient(client: BlobClient, failed: (error: unknown) => void): BlobClient {
  return {
    ...client,
    fetch: async (hash) => {
      try {
        const res = await client.fetch(hash);
        if (!res.ok)
          failed(new PhotoFetchError(`photo download failed: ${res.status}`, res.status));
        return res;
      } catch (err) {
        failed(err);
        throw err;
      }
    },
  };
}

/** A thumbnail's URL: this tab's own bytes for a photo it uploaded, else a download. */
export function useThumbnailUrl(hash: string | undefined): string | undefined {
  const own = sessionThumbUrl(hash);
  const download = own ? undefined : hash;
  const { failed } = useDownloadRetry(download);
  // `failed` is new for each attempt, and useBlobUrl fetches again when its client changes.
  const client = useMemo(() => reportingClient(getBlobClient(), failed), [failed]);
  const downloaded = useBlobUrl(download, client);
  return own ?? downloaded;
}

/** Object URL of a photo's bytes still queued on this device; `done` once the queue answered. */
export function useLocalBytesUrl(
  photoId: string,
  enabled: boolean,
): { url: string | undefined; done: boolean } {
  const { localBytes } = usePhotoUploads();
  const [state, setState] = useState<{ url: string | undefined; done: boolean }>({
    url: undefined,
    done: !enabled,
  });
  useEffect(() => {
    if (!enabled) {
      setState({ url: undefined, done: true });
      return;
    }
    let cancelled = false;
    let url: string | undefined;
    setState({ url: undefined, done: false });
    localBytes(photoId).then(
      (bytes) => {
        if (cancelled) return;
        url = bytes ? URL.createObjectURL(bytes) : undefined;
        setState({ url, done: true });
      },
      (err: unknown) => {
        console.warn('queued photo bytes unreadable', photoId, err);
        if (!cancelled) setState({ url: undefined, done: true });
      },
    );
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL?.(url);
    };
  }, [photoId, enabled, localBytes]);
  return state;
}

/**
 * One original's object URL, revoked when the hash changes or the caller unmounts. `failed`
 * stays set while a retry is pending.
 */
export function useOriginalUrl(hash: string | undefined): {
  url: string | undefined;
  failed: boolean;
} {
  const [state, setState] = useState<{ url: string | undefined; failed: boolean }>({
    url: undefined,
    failed: false,
  });
  const { attempt, failed } = useDownloadRetry(hash);
  useEffect(() => {
    if (attempt === 0) setState({ url: undefined, failed: false });
    if (!hash) return;
    let cancelled = false;
    let url: string | undefined;
    fetchPhotoBlob(hash).then(
      (blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setState({ url, failed: false });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({ url: undefined, failed: true });
        failed(err);
      },
    );
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL?.(url);
    };
  }, [hash, attempt, failed]);
  return state;
}
